// Where the stadiums are.
//
// Thirty-eight venues, hand-curated, because nothing free and commercially
// usable publishes NFL stadium coordinates and nflverse's schedule carries only a
// name and a `stadium_id`. Weather needs a point on the earth, so somebody has
// to supply one.
//
// WHY THIS IS A SOURCE AND NOT A CONSTANT. Coordinates in a sport package would
// work and would be shorter. They would also have no `observed_at`, no trust
// rank and no provenance, so the day a real venue database turns up it could
// not supersede them without a code change — and nothing could ever tell you
// this table was five years stale. Everything else in the Info Manager earns
// its place by carrying its own history; this does too.
//
// HOW THESE WERE CHECKED. Every coordinate came from Wikipedia's coordinates
// API, then all thirty-eight were run against `api.weather.gov/points`, which
// echoes back the place it resolved to. All thirty US venues resolved inside
// the right state and the right forecast grid; all eight international venues
// returned 404, which is the answer that matters — the National Weather Service
// covers the United States and its territories, and nothing else.
//
// `nwsCovered: false` is therefore recorded rather than discovered. The
// alternative is asking Washington about Wembley four times a day, forever, to
// be told 404 each time.

/** One venue, as we know it. `id` is nflverse's `stadium_id`. */
export interface VenueRecord {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  city: string;
  /** A US state code, or the country where the venue is not in the US. */
  region: string;
  country: string;
  nwsCovered: boolean;
}

/**
 * Every venue the 2026 season visits.
 *
 * Ordered by id so the generated capture is byte-stable: an unstable order
 * would change the content hash on every run and re-extract the whole file to
 * discover nothing had changed.
 */
export const VENUES: readonly VenueRecord[] = [
  {
    id: 'ATL97',
    name: 'Mercedes-Benz Stadium',
    latitude: 33.7556,
    longitude: -84.4,
    city: 'Atlanta',
    region: 'GA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'BAL00',
    name: 'M&T Bank Stadium',
    latitude: 39.2781,
    longitude: -76.6228,
    city: 'Baltimore',
    region: 'MD',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'BOS00',
    name: 'Gillette Stadium',
    latitude: 42.091,
    longitude: -71.264,
    city: 'Foxborough',
    region: 'MA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'BUF00',
    name: 'Highmark Stadium',
    latitude: 42.7731,
    longitude: -78.7922,
    city: 'Orchard Park',
    region: 'NY',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'CAR00',
    name: 'Bank of America Stadium',
    latitude: 35.2258,
    longitude: -80.8528,
    city: 'Charlotte',
    region: 'NC',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'CHI98',
    name: 'Soldier Field',
    latitude: 41.8623,
    longitude: -87.6167,
    city: 'Chicago',
    region: 'IL',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'CIN00',
    name: 'Paycor Stadium',
    latitude: 39.095,
    longitude: -84.516,
    city: 'Cincinnati',
    region: 'OH',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'CLE00',
    name: 'Huntington Bank Field',
    latitude: 41.5061,
    longitude: -81.6994,
    city: 'Cleveland',
    region: 'OH',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'DAL00',
    name: 'AT&T Stadium',
    latitude: 32.7478,
    longitude: -97.0928,
    city: 'Arlington',
    region: 'TX',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'DEN00',
    name: 'Empower Field at Mile High',
    latitude: 39.7439,
    longitude: -105.02,
    city: 'Denver',
    region: 'CO',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'DET00',
    name: 'Ford Field',
    latitude: 42.34,
    longitude: -83.0456,
    city: 'Detroit',
    region: 'MI',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'GNB00',
    name: 'Lambeau Field',
    latitude: 44.5014,
    longitude: -88.0622,
    city: 'Green Bay',
    region: 'WI',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'HOU00',
    name: 'NRG Stadium',
    latitude: 29.6847,
    longitude: -95.4108,
    city: 'Houston',
    region: 'TX',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'IND00',
    name: 'Lucas Oil Stadium',
    latitude: 39.7601,
    longitude: -86.1638,
    city: 'Indianapolis',
    region: 'IN',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'JAX00',
    name: 'EverBank Stadium',
    latitude: 30.3239,
    longitude: -81.6375,
    city: 'Jacksonville',
    region: 'FL',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'KAN00',
    name: 'GEHA Field at Arrowhead Stadium',
    latitude: 39.0489,
    longitude: -94.4839,
    city: 'Kansas City',
    region: 'MO',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'LAX01',
    name: 'SoFi Stadium',
    latitude: 33.953,
    longitude: -118.339,
    city: 'Inglewood',
    region: 'CA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'LON00',
    name: 'Wembley Stadium',
    latitude: 51.5556,
    longitude: -0.2794,
    city: 'London',
    region: 'United Kingdom',
    country: 'United Kingdom',
    nwsCovered: false,
  },
  {
    id: 'LON02',
    name: 'Tottenham Hotspur Stadium',
    latitude: 51.6044,
    longitude: -0.0664,
    city: 'London',
    region: 'United Kingdom',
    country: 'United Kingdom',
    nwsCovered: false,
  },
  {
    id: 'MAD01',
    name: 'Santiago Bernabeu Stadium',
    latitude: 40.4531,
    longitude: -3.6884,
    city: 'Madrid',
    region: 'Spain',
    country: 'Spain',
    nwsCovered: false,
  },
  {
    id: 'MEL00',
    name: 'Melbourne Cricket Ground',
    latitude: -37.82,
    longitude: 144.9833,
    city: 'Melbourne',
    region: 'Australia',
    country: 'Australia',
    nwsCovered: false,
  },
  {
    id: 'MEX00',
    name: 'Estadio Banorte',
    latitude: 19.303,
    longitude: -99.1505,
    city: 'Mexico City',
    region: 'Mexico',
    country: 'Mexico',
    nwsCovered: false,
  },
  {
    id: 'MIA00',
    name: 'Hard Rock Stadium',
    latitude: 25.9581,
    longitude: -80.2389,
    city: 'Miami Gardens',
    region: 'FL',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'MIN01',
    name: 'U.S. Bank Stadium',
    latitude: 44.974,
    longitude: -93.258,
    city: 'Minneapolis',
    region: 'MN',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'MUN01',
    name: 'FC Bayern Munich Stadium',
    latitude: 48.2188,
    longitude: 11.6248,
    city: 'Munich',
    region: 'Germany',
    country: 'Germany',
    nwsCovered: false,
  },
  {
    id: 'NAS00',
    name: 'Nissan Stadium',
    latitude: 36.1664,
    longitude: -86.7714,
    city: 'Nashville',
    region: 'TN',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'NOR00',
    name: 'Caesars Superdome',
    latitude: 29.9508,
    longitude: -90.0811,
    city: 'New Orleans',
    region: 'LA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'NYC01',
    name: 'MetLife Stadium',
    latitude: 40.8135,
    longitude: -74.0744,
    city: 'East Rutherford',
    region: 'NJ',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'PAR00',
    name: 'Stade de France',
    latitude: 48.9244,
    longitude: 2.36,
    city: 'Saint-Denis',
    region: 'France',
    country: 'France',
    nwsCovered: false,
  },
  {
    id: 'PHI00',
    name: 'Lincoln Financial Field',
    latitude: 39.9008,
    longitude: -75.1675,
    city: 'Philadelphia',
    region: 'PA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'PHO00',
    name: 'State Farm Stadium',
    latitude: 33.528,
    longitude: -112.263,
    city: 'Glendale',
    region: 'AZ',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'PIT00',
    name: 'Acrisure Stadium',
    latitude: 40.4467,
    longitude: -80.0158,
    city: 'Pittsburgh',
    region: 'PA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'RIO00',
    name: 'Maracana Stadium',
    latitude: -22.9122,
    longitude: -43.2303,
    city: 'Rio de Janeiro',
    region: 'Brazil',
    country: 'Brazil',
    nwsCovered: false,
  },
  {
    id: 'SEA00',
    name: 'Lumen Field',
    latitude: 47.5952,
    longitude: -122.3316,
    city: 'Seattle',
    region: 'WA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'SFO01',
    name: "Levi's Stadium",
    latitude: 37.403,
    longitude: -121.97,
    city: 'Santa Clara',
    region: 'CA',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'TAM00',
    name: 'Raymond James Stadium',
    latitude: 27.9758,
    longitude: -82.5033,
    city: 'Tampa',
    region: 'FL',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'VEG00',
    name: 'Allegiant Stadium',
    latitude: 36.0906,
    longitude: -115.1839,
    city: 'Paradise',
    region: 'NV',
    country: 'United States',
    nwsCovered: true,
  },
  {
    id: 'WAS00',
    name: 'Northwest Stadium',
    latitude: 38.9078,
    longitude: -76.8644,
    city: 'Landover',
    region: 'MD',
    country: 'United States',
    nwsCovered: true,
  },
];

/** Lookup by nflverse stadium id. */
export const VENUE_BY_ID: ReadonlyMap<string, VenueRecord> = new Map(VENUES.map((v) => [v.id, v]));
